import * as THREE from 'three';

/**
 * The air between the camera and everything it looks at: one model of the sky, used by the
 * dome, by the fog on every material in the scene and by the environment map, so the three
 * can never disagree about what colour the horizon is.
 *
 * That used to be three hand-matched colours — the dome's horizon, the fog, the clear colour —
 * and "matched" meant one hex value copied into three places (DECISIONS.md §13). It was
 * enough while the sky was a flat gradient and fog was a flat colour. It is not enough once
 * either has any structure: a sun low in the west makes the haze in the west glow and the
 * haze in the east blue, and a building dissolving into the west has to dissolve into the
 * glow, not into an average. So the haze is no longer a colour at all. Every fragment asks
 * `atmoSky` what the sky looks like *in the direction it is being seen from*, and fades
 * towards that.
 *
 * **How it reaches every material.** The fog is three's own fog chunks, replaced wholesale
 * before anything compiles, so every built-in material — Lambert, Standard, Basic, sprites,
 * points, the GLB car — gets it without being touched. The fog chunks need more than three's
 * fog uniforms carry (a sun direction, two sky colours), and three gives a built-in material
 * a *clone* of `ShaderLib`'s uniforms when it compiles, so a value added there is frozen at
 * whatever it was then. Except that the clone only copies what it recognises: a `Vector3` or
 * a `Color` is cloned, while a plain object is copied by reference. So the values below are
 * plain `{x, y, z}` objects — which three's uniform upload accepts for a `vec3` — and every
 * material in the scene ends up pointing at the same three numbers. One write moves the sun
 * for the whole city. It is a reliance on how `cloneUniforms` is written, and that is why it
 * is spelled out here: if three ever starts cloning plain objects, the symptom is fog that
 * stops following the weather, and this is where to look.
 *
 * **The fog itself.** Exponential-squared in the distance between `fogNear` and `fogFar`
 * rather than linear, so it starts gently and closes in, and it reaches 97% at `fogFar`,
 * which is what still hides the edge of the region. It also thins with height, integrated
 * along the ray as real haze does, so from the aerial camera the ground below is clearer
 * than the ground at the horizon, and a tower's top stands out of the murk its foot is in.
 */

/** A `vec3` uniform value three will upload but not clone. See above. */
interface Vec {
  x: number;
  y: number;
  z: number;
}

const vec = (): Vec => ({ x: 0, y: 0, z: 0 });

export const ATMOSPHERE = {
  /** Unit vector towards the sun (or the stand-in moon), world space. */
  atmoSunDir: { value: vec() },
  /** Linear RGB, packed in x/y/z. */
  atmoZenith: { value: vec() },
  atmoHorizon: { value: vec() },
  /** The colour right at, and below, the horizon line — where distant ground ends up. */
  atmoHaze: { value: vec() },
  /** Light scattered forward round the sun, already scaled by its strength. */
  atmoGlow: { value: vec() },
  /** x: 1 / scale height of the haze in metres. */
  atmoShape: { value: { x: 1 / 700, y: 0, z: 0 } },
};

/**
 * The sky in a direction. Shared by the dome, the fog and the environment bake.
 *
 * Three bands — haze at and below the horizon, a brighter horizon just above it, the zenith
 * overhead — and a forward-scattering halo round the sun that is strongest near the horizon,
 * which is where a real one is: the low sun shines through the most air.
 *
 * Guarded, because a material that reflects the sky includes it as well as getting it with
 * the fog, and GLSL will not declare a uniform twice.
 */
export const ATMOSPHERE_GLSL = /* glsl */ `
#ifndef ATMOSPHERE
#define ATMOSPHERE
uniform vec3 atmoSunDir;
uniform vec3 atmoZenith;
uniform vec3 atmoHorizon;
uniform vec3 atmoHaze;
uniform vec3 atmoGlow;
uniform vec3 atmoShape;

vec3 atmoSky( vec3 dir ) {
	float h = dir.y;
	vec3 band = mix( atmoHaze, atmoHorizon, smoothstep( -0.015, 0.12, h ) );
	vec3 sky = mix( band, atmoZenith, pow( clamp( h, 0.0, 1.0 ), 0.6 ) );
	float mu = max( dot( dir, atmoSunDir ), 0.0 );
	float halo = 0.28 * pow( mu, 5.0 ) + 0.72 * pow( mu, 42.0 );
	return sky + atmoGlow * halo * ( 1.0 - 0.65 * clamp( h, 0.0, 1.0 ) );
}
#endif
`;

const FOG_PARS_VERTEX = /* glsl */ `
#ifdef USE_FOG
	varying vec3 vFogView;
#endif
`;

const FOG_VERTEX = /* glsl */ `
#ifdef USE_FOG
	vFogView = mvPosition.xyz;
#endif
`;

const FOG_PARS_FRAGMENT = /* glsl */ `
#ifdef USE_FOG
	uniform vec3 fogColor;
	uniform float fogNear;
	uniform float fogFar;
	varying vec3 vFogView;
	${ATMOSPHERE_GLSL}
#endif
`;

/**
 * Runs after tone mapping and the sRGB encode when drawing straight to the canvas, and
 * before both when drawing into the post-processing target — that is where three puts the
 * fog chunk. So the sky colour is put through whatever the fragment itself has just been
 * put through, or a canvas frame would mix linear haze into an encoded city and the horizon
 * would come out a different colour on the low setting than on the high one.
 */
const FOG_FRAGMENT = /* glsl */ `
#ifdef USE_FOG
	// Camera to fragment in world space: the transpose of the view rotation is its inverse,
	// and the length survives because a rotation keeps it.
	vec3 fogRay = ( vec4( vFogView, 0.0 ) * viewMatrix ).xyz;
	float fogDist = length( fogRay );
	vec3 fogDir = fogRay / max( fogDist, 1e-4 );

	// Mean of exp(-y / H) along the ray, so haze thins with height.
	float fogYa = max( cameraPosition.y + fogRay.y, 0.0 ) * atmoShape.x;
	float fogYb = max( cameraPosition.y, 0.0 ) * atmoShape.x;
	float fogEa = exp( -fogYa );
	float fogDy = fogYb - fogYa;
	float fogMean = abs( fogDy ) > 1e-3 ? ( fogEa - exp( -fogYb ) ) / fogDy : fogEa;

	// 1.9 puts 97% at fogFar and about what the old linear fog had halfway there.
	float fogT = max( fogDist - fogNear, 0.0 ) / max( fogFar - fogNear, 1.0 ) * 1.9;
	float fogFactor = 1.0 - exp( -fogT * fogT * fogMean );

	// Below the horizon the haze is the horizon's colour: distant ground seen from above
	// fades into the same band the dome draws there.
	vec3 fogSky = atmoSky( normalize( vec3( fogDir.x, max( fogDir.y, 0.0 ), fogDir.z ) ) );
	// fogColor is the lightning: white mixed in by the flash, black otherwise.
	fogSky += fogColor;
	#ifdef TONE_MAPPING
		fogSky = toneMapping( fogSky );
	#endif
	fogSky = linearToOutputTexel( vec4( fogSky, 1.0 ) ).rgb;
	gl_FragColor.rgb = mix( gl_FragColor.rgb, fogSky, fogFactor );
#endif
`;

let installed = false;

/**
 * Swaps the fog chunks and gives every built-in material the shared uniforms. Must run
 * before the first material compiles; calling it again is harmless.
 */
export function installAtmosphere(): void {
  if (installed) return;
  installed = true;
  THREE.ShaderChunk.fog_pars_vertex = FOG_PARS_VERTEX;
  THREE.ShaderChunk.fog_vertex = FOG_VERTEX;
  THREE.ShaderChunk.fog_pars_fragment = FOG_PARS_FRAGMENT;
  THREE.ShaderChunk.fog_fragment = FOG_FRAGMENT;
  for (const shader of Object.values(THREE.ShaderLib)) {
    if (!('fogColor' in shader.uniforms)) continue;
    Object.assign(shader.uniforms, ATMOSPHERE);
  }
}

const _c = new THREE.Color();

function setLinear(target: Vec, hex: number, scale = 1): void {
  // `setHex` converts from sRGB into three's linear working space.
  _c.setHex(hex);
  target.x = _c.r * scale;
  target.y = _c.g * scale;
  target.z = _c.b * scale;
}

export interface AtmosphereColors {
  zenith: number;
  horizon: number;
  haze: number;
  /** Colour of the glow round the sun, and how bright — 0 for none. */
  glow: number;
  glowStrength: number;
  /** Multiplies every sky colour, so the sky can sit in the same range as the sunlit city. */
  skyIntensity: number;
}

export function setAtmosphere(colors: AtmosphereColors, sunDirection: THREE.Vector3): void {
  const { atmoZenith, atmoHorizon, atmoHaze, atmoGlow, atmoSunDir } = ATMOSPHERE;
  setLinear(atmoZenith.value, colors.zenith, colors.skyIntensity);
  setLinear(atmoHorizon.value, colors.horizon, colors.skyIntensity);
  setLinear(atmoHaze.value, colors.haze, colors.skyIntensity);
  setLinear(atmoGlow.value, colors.glow, colors.glowStrength);
  atmoSunDir.value.x = sunDirection.x;
  atmoSunDir.value.y = sunDirection.y;
  atmoSunDir.value.z = sunDirection.z;
}
